import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/Shared";
import { formatINR, timeAgo } from "@/lib/format";
import { toast } from "@/components/ui/sonner";
import { GripVertical } from "lucide-react";

// Generic status-column board for Quotes / Sales Orders / Invoices / Customers.
// Pass `canMove(row, colId)` + `onMove(row, colId)` to enable drag & drop between columns.
export default function KanbanBoard({ columns, rows, groupKey = "status", title, subtitle, sumKey = "grand_total", meta, testid = "kanban", canMove, onMove }) {
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);
  const colOf = (r) => (typeof groupKey === "function" ? groupKey(r) : r[groupKey]);
  const dnd = Boolean(canMove && onMove);
  const drop = (col, allowed, blocked) => {
    setOver(null);
    if (drag && allowed) onMove(drag, col.id);
    else if (drag && blocked) toast.error(`Can't move ${drag.number || "this card"} from ${colOf(drag)} to ${col.id}`);
    setDrag(null);
  };
  return (
    <div className="flex gap-3 overflow-x-auto pb-4 snap-x snap-mandatory sm:snap-proximity no-scrollbar sm:[scrollbar-width:auto]" data-testid={testid}>
      {columns.map((col) => {
        const items = rows.filter((r) => colOf(r) === col.id);
        const allowed = Boolean(drag && colOf(drag) !== col.id && canMove(drag, col.id));
        const blocked = Boolean(drag && colOf(drag) !== col.id && !allowed);
        const draggable = (r) => dnd && columns.some((c) => c.id !== col.id && canMove(r, c.id));
        return <KanbanColumn key={col.id} col={col} items={items} testid={testid} sumKey={sumKey} title={title} subtitle={subtitle} meta={meta}
          allowed={allowed} blocked={blocked} over={over === col.id} dragging={drag} draggable={draggable}
          onOver={(ev) => { if (dnd && allowed) { ev.preventDefault(); ev.dataTransfer.dropEffect = "move"; } setOver(col.id); }}
          onLeave={() => setOver(null)} onDrop={(ev) => { ev.preventDefault(); drop(col, allowed, blocked); }}
          onDragStart={setDrag} onDragEnd={() => { setDrag(null); setOver(null); }} />;
      })}
    </div>
  );
}

function KanbanColumn({ col, items, testid, sumKey, title, subtitle, meta, allowed, blocked, over, dragging, draggable, onOver, onLeave, onDrop, onDragStart, onDragEnd }) {
  const navigate = useNavigate();
  const total = sumKey ? items.reduce((s, r) => s + (r[sumKey] || 0), 0) : 0;
  const slug = col.id.replace(/\s+/g, "-").toLowerCase();
  const zone = over && allowed ? "bg-[#0B7C5A]/10 ring-2 ring-[#0B7C5A]/40" : over && blocked ? "bg-[#F83A84]/5 ring-2 ring-[#F83A84]/30" : "bg-[#F5F6FA]";
  return (
    <div className="w-[84vw] sm:w-72 shrink-0 snap-center sm:snap-start" data-testid={`${testid}-col-${slug}`} onDragOver={onOver} onDragLeave={onLeave} onDrop={onDrop}>
      <div className="flex items-center justify-between px-1 mb-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-[#101D51] flex items-center gap-1">
          <span className="w-2 h-2 rounded-full" style={{ background: col.color || "#101D51" }} />{col.label || col.id}
          <span className="ml-1 bg-[#E6E8F0] text-[#6B7189] rounded-full px-1.5 text-[10px]">{items.length}</span>
        </div>
        {total > 0 && <span className="text-[11px] text-[#6B7189]">{formatINR(total)}</span>}
      </div>
      <div className={`space-y-2 min-h-[80px] rounded-xl p-2 transition-colors ${zone}`} data-testid={`${testid}-drop-${slug}`}>
        {items.map((r) => <KanbanCard key={r.id} r={r} col={col} testid={testid} sumKey={sumKey} title={title} subtitle={subtitle} meta={meta}
          draggable={draggable(r)} dimmed={dragging?.id === r.id} onDragStart={onDragStart} onDragEnd={onDragEnd} onClick={() => navigate(r.path)} />)}
        {items.length === 0 && <div className="text-xs text-[#6B7189] text-center py-6">{dragging && allowed ? "Drop here" : "—"}</div>}
      </div>
    </div>);
}

function KanbanCard({ r, col, testid, sumKey, title, subtitle, meta, draggable, dimmed, onDragStart, onDragEnd, onClick }) {
  return (
    <Card onClick={onClick} data-testid={`${testid}-card-${r.number || r.id}`} draggable={draggable}
      onDragStart={(ev) => { ev.dataTransfer.setData("text/plain", r.id); ev.dataTransfer.effectAllowed = "move"; onDragStart(r); }} onDragEnd={onDragEnd}
      className={`p-3 cursor-pointer hover:shadow-md transition-shadow relative overflow-hidden ${draggable ? "cursor-grab active:cursor-grabbing" : ""} ${dimmed ? "opacity-40" : ""}`}>
      <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: col.color || "#101D51" }} />
      <div className="pl-2">
        <div className="flex items-center justify-between gap-2"><span className="text-xs font-mono text-[#6B7189] flex items-center gap-1">{draggable && <GripVertical className="w-3 h-3 text-[#C7CBDA]" />}{r.number || ""}</span><span className="text-[11px] text-[#6B7189]">{timeAgo(r.date || r.created_at)}</span></div>
        <div className="font-medium text-[#101D51] text-sm mt-1 truncate">{title(r)}</div>
        {subtitle && <div className="text-xs text-[#6B7189] mt-0.5 truncate">{subtitle(r)}</div>}
        <div className="flex items-center justify-between mt-2 text-[11px] text-[#6B7189]">
          <span>{meta ? meta(r) : ""}</span>
          {sumKey && <span className="font-semibold text-[#101D51]">{formatINR(r[sumKey] || 0)}</span>}
        </div>
      </div>
    </Card>);
}

export function ModeToggle({ mode, setMode, testid = "mode-toggle" }) {
  return (
    <div className="flex rounded-lg border border-[#E6E8F0] overflow-hidden" data-testid={testid}>
      {[["board", "Board"], ["list", "List"]].map(([m, l]) => (
        <button key={m} onClick={() => setMode(m)} data-testid={`${testid}-${m}`} className={`h-9 px-3 text-xs font-medium ${mode === m ? "bg-[#101D51] text-white" : "bg-white text-[#6B7189]"}`}>{l}</button>))}
    </div>);
}

export function useMode(key) {
  const k = `tradedesk.mode.${key}`;
  const get = () => localStorage.getItem(k) || "list";
  return [get, (m) => localStorage.setItem(k, m)];
}
